<template>
  <div class="full-sidebar">
    <div v-if="!!user" class="first-section">
      <div class="avatar-section">
        <div class="avatar-container">
          <img
            v-if="user.avatar_url"
            class="rounded-circle"
            :src="user.avatar_url + '?' + Date.now()"
            alt="Avatar"
          />
          <div v-else class="img-placeholder">
            <i class="material-icons align-middle">account_circle</i>
          </div>
        </div>

        <div class="avatar-data">
          <div class="name">{{ user.name }}</div>
          <div class="email">{{ user.username }}</div>
        </div>
      </div>

      <div class="form-group">
        <BaseText variant="inputLabel">User Name</BaseText>
        <input type="text" name="name" id="name" v-model="name" class="form-control" />
      </div>

      <div class="form-group">
        <BaseText variant="inputLabel">Email</BaseText>
        <input type="text" name="name" id="name" v-model="userName" class="form-control" disabled />
      </div>

      <div class="form-group">
        <BaseText variant="inputLabel">Role</BaseText>
        <select type="text" name="userRole" id="userRole" v-model="userRole" class="form-control">
          <option v-for="(role, index) in roles" :key="index" :value="role.id">
            {{ role.name }}
          </option>
        </select>
      </div>

      <div class="form-check">
        <input
          v-model="userIsLocalAdmin"
          type="checkbox"
          id="radioLocalAdmin"
          class="form-check-input mr-2"
        />
        <BaseText variant="inputLabel">Local administrator</BaseText>
      </div>
      <div class="form-check" v-if="isAdmin">
        <input
          v-model="userIsAdmin"
          type="checkbox"
          id="radioAdmin"
          class="form-check-input mr-2"
        />
        <BaseText variant="inputLabel">Super administrator</BaseText>
      </div>

      <div class="content-section">
        <div class="description-container">
          <div class="description-item">
            <span>Created: </span>{{ formatDate(user.created, 'date') }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="user" class="button-container">
      <div class="button">
        <button v-if="isAdmin" type="button" class="button-2-secondary" @click="handlePreviewMode">
          Preview PicFlow as this user
        </button>
      </div>
      <div class="button">
        <button type="button" class="button-2-secondary" @click="openDeleteUserModal">
          <i class="material-icons-outlined align-middle">delete_outline</i>
          Delete User
        </button>
      </div>

      <div class="button">
        <button type="button" class="button-2-primary" @click="updateUser" :disabled="isSaving">
          <i v-if="isSaving" class="material-icons align-middle spin-animation">refresh</i>
          <i v-else class="material-icons-outlined align-middle">save</i>
          Save
        </button>
      </div>

      <DeleteUserModal
        @closeModal="showDeleteUserModal = false"
        :user="user"
        v-if="showDeleteUserModal"
        @deleted="$emit('userDeleted')"
      />
    </div>
  </div>
</template>

<script>
  import SidebarContainer from '@/components/common/SidebarContainer.vue';
  import DeleteUserModal from '../../DeleteUserModal.vue';
  import { SET_ORGANISATIONS } from '@/store/actions/organisation';
  import { VIEW_AS_USER } from '@/store/actions/auth';
  import { ADMIN_USER_UPDATE, USER_UPDATE } from '@/store/actions/user';
  import { SET_USER_LOCAL_ADMIN } from '@/store/actions/auth';
  import BaseText from '@ui/atoms/baseText/BaseText.vue';
  import { globalFiltersMixin } from '@/helpers/mixins';
  import { NOT_ORGANISATION_ADMIN_MESSAGE } from '@/constant/const';

  export default {
    name: 'UsersSidebar',

    components: { SidebarContainer, DeleteUserModal, BaseText },

    props: {
      user: {
        type: Object,
        default: () => null,
      },
      organisation: {
        type: Object,
        required: true,
      },
    },

    mixins: [globalFiltersMixin],

    data() {
      return {
        isSaving: false,
        showRoles: false,
        showInfo: false,
        showRolesModal: false,
        selectedRole: null,
        showDeleteUserModal: false,
        userRole: this.user?.role,
        userIsLocalAdmin: this.user?.is_local_administrator,
        userIsAdmin: this.user?.is_administrator,
        name: this.user?.name,
        userName: this.user?.username,
      };
    },

    watch: {
      user() {
        this.userRole = this.user?.role;
        this.name = this.user?.name;
        this.userName = this.user?.username;
        this.userIsLocalAdmin = this.user?.is_local_administrator;
        this.userIsAdmin = this.user?.is_administrator;
      },
    },

    computed: {
      isOrganizationAdmin() {
        return this.$store.getters.isOrganizationAdmin;
      },

      roles() {
        return this.$store.state.roles.roles || [];
      },

      isAdmin() {
        return this.$store.getters.isAdministrator;
      },

      userId() {
        return this.$store.getters.getUserId;
      },
    },

    methods: {
      async updateUser() {
        this.isSaving = true;
        try {
          const data = {
            userData: {
              role: this.userRole,
              user_id: this.user.user_id,
              is_local_administrator: this.userIsLocalAdmin,
              is_administrator: this.userIsAdmin,
              name: this.name,
            },
            userId: this.user.user_id,
          };

          let updatedUser;
          if (this.isAdmin) {
            updatedUser = await this.$store.dispatch(ADMIN_USER_UPDATE, data);
          } else {
            updatedUser = await this.$store.dispatch(USER_UPDATE, data.userData);
          }

          if (updatedUser.data.user_id === this.userId) {
            await this.$store.dispatch(SET_USER_LOCAL_ADMIN, updatedUser.data);
            if (!updatedUser.data.is_local_administrator && !updatedUser.data.is_administrator) {
              this.$router.push({ name: 'Dashboard' });
              this.$toasted.global.general_error({
                message: NOT_ORGANISATION_ADMIN_MESSAGE,
              });
            }
          }

          this.$toasted.global.general_success({
            message: 'User updated',
          });
          this.$emit('userUpdated');
        } catch (error) {
          this.$toasted.global.general_error({
            message: error.response?.data?.message || String(error),
          });
        }
        this.isSaving = false;
      },

      openDeleteUserModal() {
        this.showDeleteUserModal = true;
      },

      handlePreviewMode() {
        this.$store.commit(SET_ORGANISATIONS, [this.selectedOrganisation]);
        this.$store.dispatch(VIEW_AS_USER, this.user).then(() => {
          this.$router.push({ name: 'Organisation' });
          window.location.reload();
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .first-section {
    max-height: 80%;
    overflow: scroll;
    padding-bottom: 20px;
  }
  .full-sidebar {
    overflow: hidden;
    position: relative;
    height: 100%;
    background-color: #fff;
    width: 20vw;
    max-width: 20vw;
    min-width: 20vw;
    font-size: 14px;
    padding: 20px;
    margin-left: 8px;
  }

  .content-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    padding-bottom: 20px;
  }

  .form-group {
    margin-bottom: 8px;

    label {
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
      gap: 1px;

      font-weight: 500;
      line-height: 24px;

      .label {
        display: flex;
        gap: 8px;
        align-items: center;

        font-size: 14px;
      }

      input,
      select {
        padding: 8px 16px;

        border: 2px solid $borderGrey;
        border-radius: 8px;
        font-size: 16px;
      }
    }
  }

  .button-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: #fff;
    position: absolute;
    bottom: 20px;
    left: 20px;
    right: 20px;
  }

  button {
    width: 100%;
  }
  .dropdown-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 8px;
    gap: 8px;
    width: 100%;

    .dropdown {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      font-size: 16px;
      line-height: 24px;
      padding: 8px 0;
      font-weight: 500;
      cursor: pointer;
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-bottom: 16px;
    border-bottom: 2px solid $lineGrey;
    overflow-x: hidden;
  }

  .description-container {
    display: flex;
    flex-direction: column;
    padding: 12px 2px;
    gap: 8px;

    .description-item {
      font-weight: 500;
      font-size: 14px;
      line-height: 24px;
      color: $primaryText;

      a {
        color: $primaryText;
      }

      span {
        color: $secondaryText;
        font-weight: 400;
      }
    }
  }

  .avatar-section {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: hidden;
    margin-bottom: 6px;

    .avatar-container {
      min-width: 60px;
      min-height: 60px;

      img {
        width: 80px;
        background-color: $fillGrey;
      }

      i {
        font-size: 60px;
      }
    }

    .avatar-data {
      display: flex;
      flex-direction: column;
      gap: 8px;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;

      .name {
        color: $primaryText;
        padding-bottom: 2px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        font-family: Poppins;
        font-weight: 500;
        font-size: 16px;
        line-height: 100%;
        letter-spacing: 0%;
      }

      .email {
        color: $secondaryText;
        font-family: Poppins;
        font-weight: 400;
        font-size: 16px;
        line-height: 100%;
        letter-spacing: 0%;
      }
    }
  }

  td {
    height: 100%;
    vertical-align: middle;
  }

  tr {
    height: 100%;
  }
</style>
